<script lang="ts" setup>
import { getIncidents, deleteIncidents } from '@/app/core/services/incidents'
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { object, array, date } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading,
  DTable,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  getEmployees,
  getConcepts,
  getProcesses
} from '@/app/core/services/pending-processes'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos Listas */
const itemsProcesses = ref([])
const searchProcesses = ref('')
let allItemsConcepts: any[] = []
const itemsConcepts = ref()
const itemsEmployees = ref<string[]>([])
const observationError = ref()

/* Datos componente table */
const columns = ref([
  { name: 'employee', title: t('incidents.list.employee') },
  { name: 'concept', title: t('incidents.list.concept') },
  { name: 'typeProcessCode', title: t('incidents.list.process') },
  { name: 'startDate', title: t('incidents.list.startDate') },
  { name: 'endDate', title: t('incidents.list.endDate') },
  { name: 'quantity', title: t('incidents.list.quantity') },
  { name: 'amount_i', title: t('incidents.list.amount') },
  { name: 'percentage_i', title: t('incidents.list.percentage') }
])
const selectedTable = ref()
const rows = ref([] as any[])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')
const tableDeleteIncident = ref()
const formatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 5,
  minimumFractionDigits: 2
})

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  startDate: date().required(
    t('form.valid.fieldRequired', { field: t('incidents.valid.startDate') })
  ),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('incidents.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(
          t('form.valid.fieldRequired', { field: t('incidents.valid.endDate') })
        )
    }
    return schema.required(
      t('form.valid.fieldRequired', { field: t('incidents.valid.endDate') })
    )
  }),
  employees: array()
    .min(1, t('incidents.valid.employeeSelectionRequired'))
    .required(
      t('form.valid.fieldRequired', { field: t('incidents.valid.employees') })
    )
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  validationSchema
})

/* Definicion campos del formulario */
const { value: processId } = useField<string>(ref('processId'))
const { value: concepts } = useField<any[]>(ref('concepts'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>(ref('endDate'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: observation } = useField<string>(ref('observation'))
watch(endDate, () => {
  void listEmployees(endDate.value)
})

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any, { resetForm }) => {
  void listIncidents(values)
})

/* Listar incidencias */
const listIncidents = async (values: any) => {
  loading.value = true
  messageLoading.value = ''
  const typeProcess: any = itemsProcesses.value.filter(
    (_: any) => _.id === values.processId
  )
  const p1 = new Promise(resolve => {
    resolve(
      getIncidents(
        offset.value,
        perPage.value,
        values.startDate,
        values.endDate,
        values.employees,
        values.concepts,
        typeProcess[0]?.code ?? '',
        null,
        null
      )
    )
  })
  const p2 = new Promise(resolve => {
    resolve(
      getIncidents(
        0,
        10000,
        values.startDate,
        values.endDate,
        values.employees,
        values.concepts,
        typeProcess[0]?.code ?? '',
        null,
        null
      )
    )
  })
  Promise.all([p1, p2])
    .then(async (results: any) => {
      rows.value = results[0]
      totalRows.value = results[1].length
      loading.value = false
    })
    .catch(async function (error: any) {
      notificationError(error, 'incidents', t, notifications, loading)
    })
}

/* Listar procesos */
const processes = async () => {
  let processes = await getProcesses()
  processes = processes.filter(
    (_: any) =>
      _.countries?.length <= 0 ||
      _.countries.indexOf(storePayroll.payroll?.country?.id) > -1
  )
  itemsProcesses.value = processes
}

/* Listar conceptos segun el proceso seleccionado */
const filterConcepts = (idProcess: any) => {
  concepts.value = []
  let itemsFilter: any[] = []
  itemsFilter = [...allItemsConcepts]
  itemsFilter = itemsFilter.filter(
    (_: any) => _.processes.indexOf(idProcess) > -1
  )
  itemsConcepts.value = itemsFilter
  concepts.value = itemsFilter.map((_: any) => _.id.toString())
}

/* Listar empleados segun la fecha final seleccionada */
const listEmployees = async (dateEnd: any) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    itemsEmployees.value = await getEmployees(dateEnd)
    employees.value = itemsEmployees.value.map((_: any) => _.id)
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'incidents', t, notifications, loading)
  }
}

const windowConfirmRemoveIncidence = () => {
  if (
    selectedTable.value?.length === 0 ||
    selectedTable.value?.length === undefined
  ) {
    notifications.addNotification({
      title: t('incidents.valid.minIncidentSelection'),
      type: 'error',
      time: 10000
    })
  } else {
    showModal.value = true
    const description =
      selectedTable.value?.length < 2
        ? t('incidents.notification.singleIncidentSelected')
        : t('incidents.notification.multipleIncidentsSelected', {
            cant: selectedTable.value?.length as string
          })
    descriptionModal.value = t('incidents.notification.confirmDelete', {
      description: description
    })
  }
}
const removeIncidence = async () => {
  if (observation.value === '' || observation.value === undefined) {
    observationError.value = t('incidents.valid.required')
  } else {
    loading.value = true
    observationError.value = ''
    closeModal()
    const data = {
      incidents: selectedTable.value,
      user: store.user?.name,
      observation: observation.value
    }
    await deleteIncidents(data)
      .then(response => {
        notifications.addNotification({
          title: t('incidents.notification.requestCreated'),
          text: t('incidents.notification.deletionRequestCreated'),
          type: 'success',
          time: 10000
        })
        void router.push({ name: 'ListDeleteIncident' })
      })
      .catch(async (error: any) => {
        notificationError(error, 'incidents', t, notifications, loading)
      })
  }
}

/* Validar campos de fechas (no dejar vacios) */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListDeleteIncident' })
}

onMounted(async () => {
  loading.value = true
  try {
    await processes()
    allItemsConcepts = await getConcepts()
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'incidents', t, notifications, loading)
  }
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      typeAlert="question"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal"
      @otherMethod="removeIncidence">
      <template v-slot:form>
        <div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              {{ t('incidents.delete.observation') }}
              <span class="text-red-500">*</span>
            </label>
            <p style="font-size: 12px; margin-top: -6px">
              {{ t('incidents.delete.observationValid') }}
            </p>
            <div>
              <textarea
                class="shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-transparent mt-1 block w-full sm:text-sm border border-gray-300 rounded-md p-2"
                style="resize: none"
                type="text"
                v-model="observation"
                rows="5"
                maxlength="300" />
            </div>
            <span class="text-xs text-red-500">{{ observationError }}</span>
          </div>
        </div>
      </template>
    </DModal>
    <div class="bg-gray-50 pb-12">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{ t('incidents.delete.title') }}
        </p>
      </div>
    </div>
    <form
      class="scroll overflow-auto"
      style="height: calc(100% - 7rem)"
      @submit.prevent="onSubmit">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-3">
            <div class="col-span-4 lg:col-span-2 py-1 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DTextField
                    type="date"
                    :label="t('fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    @input="validateDate"
                    :error="errors.startDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DTextField
                    type="date"
                    :label="t('fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    @input="validateDate"
                    :error="errors.endDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DSelectFieldSearch
                    :label="t('fields.processId')"
                    v-model="processId"
                    :options="filterOptions(itemsProcesses, searchProcesses)"
                    @filter="searchProcesses = $event"
                    @change="filterConcepts(processId)"
                    :error="errors.processId" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                  <DMultiselect
                    :label="t('fields.masterConcepts')"
                    v-model="concepts"
                    :headerTitle="'Conceptos'"
                    :options="itemsConcepts"
                    :error="errors.concepts" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="px-4 flex justify-between">
                <h3 class="text-md font-medium leading-6 text-gray-900">
                  {{ t('fields.employees') }}
                </h3>
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div class="flex justify-center items-center p-5 gap-5">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end">
              <DBtn
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.search')" />
            </div>
          </template>
        </DCard>
        <div
          class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0">
          <main class="px-2 py-2 flex flex-col gap-3">
            <div class="h-full px-3 py-5 overflow-auto scroll">
              <DTable
                ref="tableDeleteIncident"
                :columns="columns"
                :rows="rows"
                :total-rows="totalRows"
                :select-options="true"
                :pagination-limit="false"
                :text-no-data="t('table.noData')"
                :text-per-page="t('table.rowsPerPage')"
                @selected="selectedTable = $event"
                @perPage="perPage = $event"
                @offset="offset = $event"
                @sort="sortBy = $event"
                @changePage="onSubmit()">
                <template #employee="data: any">
                  {{ data.employeeCode + ' - ' + data.employeeName }}
                </template>
                <template #concept="data: any">
                  {{ data.conceptCode + ' - ' + data.conceptName }}
                </template>
                <template #percentage_i="data: any">
                  <div v-if="data.percentage">
                    {{
                      Number(data.percentage / 100).toLocaleString('en-us', {
                        style: 'percent',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 5
                      })
                    }}
                  </div>
                </template>
                <template #amount_i="data: any">
                  <div v-if="data.amount">
                    {{ formatter.format(data.amount) }}
                  </div>
                </template>
              </DTable>
            </div>
          </main>
          <footer class="bg-gray-50 py-3 rounded-b-lg px-5">
            <div class="flex justify-end gap-3">
              <DBtn
                type="button"
                class="font-semibold py-1 text-base"
                color="secondary"
                @click="back()"
                v-html="t('form.button.back')" />
              <DBtn
                @click.prevent="windowConfirmRemoveIncidence"
                class="px-5 py-2 font-semibold">
                {{
                  `${t('incidents.delete.delete')} ${
                    selectedTable?.length === undefined
                      ? 0
                      : selectedTable?.length
                  } ${
                    selectedTable?.length === undefined ||
                    selectedTable?.length === 1
                      ? t('incidents.delete.deleteIncident', 0)
                      : t('incidents.delete.deleteIncident', 1)
                  }`
                }}
              </DBtn>
            </div>
          </footer>
        </div>
      </div>
    </form>
  </div>
</template>
<style>
.table-scroll {
  padding-bottom: 0 !important;
}
</style>
